import { DropzoneState, useDropzone } from "react-dropzone"
import { warnMessage } from "../../../../../Actions/Client/MessagesActions"
import { upload } from "../../../../../Actions/Client/Interface/UploaderActions"
import accepts from "attr-accept"
import React, { useCallback, useState } from "react"
import { useDispatch } from "react-redux"
import { routePaths } from "../../../../../Routes"

interface MultipleOptions {
    allowMultiple: boolean
    multipleNotAllowedMessage: string
}

export interface UploaderProps {
    stateKey: string
    multiple: boolean | MultipleOptions
    accept?: string
    disabled?: boolean
    renderDropZoneContent: (state: DropzoneState) => JSX.Element
    squashSuccessMessage: boolean
    appendTimestamp: boolean
}

export const Uploader = ({
    stateKey,
    multiple,
    accept,
    disabled,
    renderDropZoneContent,
    squashSuccessMessage,
    appendTimestamp
}: UploaderProps) => {
    const [fileWithFormatError, setFileWithFormatError] = useState<string>()
    const dispatch = useDispatch()
    const handleDrop = useCallback(
        (files: File[], rejectedFiles: File[]) => {
            setFileWithFormatError(undefined)

            const allowMultiple =
                multiple === true ||
                (multiple !== false && multiple.allowMultiple)
            const validFile = files
                .concat(rejectedFiles)
                .find(file => accept === undefined || accepts(file, accept))

            if (rejectedFiles.length === 0) {
                dispatch(
                    upload(
                        stateKey,
                        files,
                        squashSuccessMessage,
                        appendTimestamp
                    )
                )
                return
            }

            if (allowMultiple || validFile === undefined) {
                // rejected because of other reasons than number of files
                setFileWithFormatError(rejectedFiles[0].name)
                return
            }

            dispatch(
                warnMessage(
                    typeof multiple === "boolean"
                        ? "Only one file is accepted here."
                        : multiple.multipleNotAllowedMessage
                )
            )

            dispatch(
                upload(
                    stateKey,
                    [validFile],
                    squashSuccessMessage,
                    appendTimestamp
                )
            )
        },
        [
            accept,
            appendTimestamp,
            dispatch,
            multiple,
            squashSuccessMessage,
            stateKey
        ]
    )

    return (
        <>
            {fileWithFormatError !== undefined && (
                <div className="bg-coral-200 p-4 rounded overflow-x-auto mb-4">
                    <p className="text-coral-700 text-sm">
                        File {fileWithFormatError} isn't in the required format.{" "}
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink-500"
                            href={routePaths.docs("input-data-types")}
                        >
                            More info here.
                        </a>
                    </p>
                </div>
            )}
            {renderDropZoneContent(
                useDropzone({
                    onDrop: handleDrop,
                    multiple:
                        multiple === true ||
                        (multiple !== false && multiple.allowMultiple),
                    accept,
                    disabled
                })
            )}
        </>
    )
}
